import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table, Modal, Pagination, message } from "antd";
import { UserContext } from "../UserContext";
import { withRouter } from "../utils/withRouter";
import api from "../api/index";
import "./styles/ViewLocations.css";

function ViewLocations({ navigate }) {
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [subagents, setSubagents] = useState([]);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sorter, setSorter] = useState({ field: null, order: null }); // order: 'ascend' | 'descend' | null

  // Helper to format full address from split fields
  const formatAddress = (row = {}) => {
    const { address, city, state, zipcode, country } = row;
    const parts = [
      address,
      city,
      state && zipcode ? `${state} ${zipcode}` : state,
      country,
    ].filter(Boolean);
    return parts.join(", ");
  };

  useEffect(() => {
    if (!user?.id || !user?.access_token) return;

    const load = async () => {
      try {
        const [res, transferRes] = await Promise.all([
          fetch(
            `${api.main}api/agent/all_locations?id=${user.id}&access_token=${user.access_token}`
          ),
          fetch(
            `${api.main}api/agent/transfers?sagent_id=${user.id}&access_token=${user.access_token}`
          ),
        ]);

        if (!res.ok) {
          throw new Error("Locations: Not authorized or server error");
        }

        const data = await res.json();

        let transferringIds = new Set();

        if (transferRes.ok) {
          const transferData = await transferRes.json();
          console.log("transferData from API:", transferData);

          const allTransfers = Array.isArray(transferData.transfers)
            ? transferData.transfers
            : [];

          // Only treat locations as "transferring" if the transfer is still pending and not expired.
          const activeTransfers = allTransfers.filter(
            (t) => t.status === "pending" && !t.is_expired
          );

          const idList = activeTransfers
            .flatMap((t) => {
              if (Array.isArray(t.location_ids)) {
                return t.location_ids;
              }

              if (t.location_id != null) {
                return [t.location_id];
              }

              if (t.location_registration_id != null) {
                return [t.location_registration_id];
              }
              if (t.registration_id != null) {
                return [t.registration_id];
              }
              if (t.location && t.location.registration_id != null) {
                return [t.location.registration_id];
              }

              // Nothing usable for this transfer
              return [];
            })
            .filter((id) => id != null)
            .map((id) => String(id));

          transferringIds = new Set(idList);
        } else {
          const errText = await transferRes.text();
          console.warn(
            "Failed to load transfer IDs:",
            transferRes.status,
            errText
          );
        }

        if (data.message) {
          setSubagents([]);
        } else {
          const formatted = (data.data || []).map((s) => {
            const isTransferring = transferringIds.has(
              String(s.registration_id)
            );
            const baseStatusLabel = s.status === "A" ? "Active" : "Inactive";

            return {
              ...s,
              isTransferring,
              statusLabel: isTransferring ? "Transferring" : baseStatusLabel,
              actionLabel: s.status === "A" ? "Deactivate" : "Activate",
            };
          });

          setSubagents(formatted);
        }
      } catch (err) {
        setError(err.message || "Failed to load locations");
        message.error(err.message || "Failed to load locations");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.id, user?.access_token]);

  // search + paging
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrent(1);
  };

  const searchedLocations = useMemo(() => {
    const q = (searchQuery || "").toLowerCase().trim();
    if (!q) return subagents;
    const tokens = q.split(/\s+/).filter(Boolean);

    return subagents.filter((l) => {
      const hay = [
        l.business_name || "",
        formatAddress(l) || "",
      ]
        .join(" ")
        .toLowerCase();

      return tokens.every((t) => hay.includes(t));
    });
  }, [subagents, searchQuery, formatAddress]);

  const sortedLocations = useMemo(() => {
    const list = [...searchedLocations];
    const { field, order } = sorter || {};
    if (!field || !order) return list;

    const dir = order === "ascend" ? 1 : -1;

    const getVal = (row, key) => {
      switch (key) {
        case "business_name":
          return (row[key] || "").toString().toLowerCase();
        case "address":
          return formatAddress(row).toLowerCase();
        case "vatm_fee":
        case "daily_cash_limit":
          return Number(row[key] ?? 0);
        case "status":
          // A = Active (1), else 0
          return row.status === "A" ? 1 : 0;
        case "license_id":
          return row.license_id ? 1 : 0;
        default:
          return row[key];
      }
    };

    list.sort((a, b) => {
      const av = getVal(a, field);
      const bv = getVal(b, field);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });

    return list;
  }, [searchedLocations, sorter, formatAddress]);

  const { pageRows, total } = useMemo(() => {
    const start = (current - 1) * pageSize;
    return {
      pageRows: sortedLocations.slice(start, start + pageSize),
      total: sortedLocations.length,
    };
  }, [sortedLocations, current, pageSize]);

  const rowByKey = useMemo(() => {
    const m = new Map();
    subagents.forEach((s) => m.set(s.registration_id, s));
    return m;
  }, [subagents]);

  // single-row actions
  const sendPasswordResetLink = async (registration_id) => {
    try {
      const res = await fetch(`${api.main}api/agent/send_password_link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ registration_id, access_token: user.access_token }),
      });
      if (!res.ok) throw new Error("Failed to send password reset link.");
      const data = await res.json();
      message.success(data.message || "A password reset link has been sent.");
    } catch (err) {
      message.error(
        err.message ||
          "Something went wrong while sending the password reset link."
      );
    }
  };

  // bulk actions
  const bulkDelete = () => {
    const ids = selectedRowKeys;
    if (!ids.length) return;

    Modal.confirm({
      title: `Delete ${ids.length} location${ids.length > 1 ? "s" : ""}?`,
      content: (
        <>
          <p>
            This action is <strong>permanent</strong> and cannot be undone.
          </p>
          <p>
            We recommend <strong>deactivating</strong> first if you might use
            these locations again.
          </p>
          <p>
            <strong>No refunds are issued for active licenses</strong>. If you
            still wish to delete, you may want to wait until the{" "}
            <strong>end of the month</strong> to avoid losing remaining license
            value.
          </p>
        </>
      ),
      okButtonProps: { className: "btn btn-danger" },
      okText: "Delete",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

          await Promise.all(
            ids.map((registration_id) =>
              fetch(`${api.main}api/agent/delete_location`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  registration_id,
                  access_token: user.access_token,
                  tz,
                }),
              }).then(async (r) => {
                if (!r.ok) {
                  // try to surface backend error message if available
                  let errText = `Delete failed (HTTP ${r.status})`;
                  try {
                    const body = await r.json();
                    if (body?.error || body?.message) {
                      errText = body.error || body.message;
                    }
                  } catch {}
                  throw new Error(errText);
                }
                return r.json();
              })
            )
          );

          setSubagents((prev) =>
            prev.filter((s) => !ids.includes(s.registration_id))
          );
          setSelectedRowKeys([]);
          message.success(
            `Deleted ${ids.length} item${ids.length > 1 ? "s" : ""}`
          );
        } catch (err) {
          message.error(
            err.message || "Bulk delete failed for one or more items."
          );
          throw err; // keeps the confirm button in loading state until this settles
        }
      },
    });
  };

  const bulkSetStatus = (targetStatus) => {
    const ids = selectedRowKeys;
    if (!ids.length) return;

    const label = targetStatus === "Active" ? "activate" : "deactivate";
    const rows = ids.map((id) => rowByKey.get(id)).filter(Boolean);
    const toChange = rows.filter((r) =>
      targetStatus === "Active" ? r.status !== "A" : r.status === "A"
    );
    if (!toChange.length) {
      message.info(`Nothing to ${label}.`);
      return;
    }

    const proceed = async () => {
      try {
        await Promise.all(
          toChange.map((row) =>
            fetch(`${api.main}api/agent/toggle_status`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                registration_id: row.registration_id,
                status: targetStatus,
                access_token: user.access_token,
              }),
            }).then((r) => {
              if (!r.ok) throw new Error("Failed to update status");
              return r.json();
            })
          )
        );
        setSubagents((prev) =>
          prev.map((s) =>
            toChange.some((r) => r.registration_id === s.registration_id)
              ? {
                  ...s,
                  status: targetStatus === "Active" ? "A" : "I",
                  statusLabel: targetStatus,
                  actionLabel:
                    targetStatus === "Active" ? "Deactivate" : "Activate",
                }
              : s
          )
        );
        setSelectedRowKeys([]);
        message.success(
          `${targetStatus}d ${toChange.length} item${
            toChange.length > 1 ? "s" : ""
          }`
        );
      } catch (err) {
        message.error(
          err.message || `Failed to ${label} some items.`
        );
      }
    };

    if (targetStatus === "Inactive") {
      Modal.confirm({
        title: `Deactivate ${toChange.length} location${
          toChange.length > 1 ? "s" : ""
        }?`,
        content: (
          <>
            <p>Deactivation is reversible — you can reactivate later.</p>
            <p>
              If you intend to remove them permanently, use{" "}
              <strong>Delete</strong> instead.
            </p>
          </>
        ),
        okText: "Deactivate",
        cancelText: "Cancel",
        okButtonProps: { className: "btn btn-primary-soft" },
        centered: true,
        onOk: proceed,
      });
    } else {
      proceed();
    }
  };

  const initiateTransfer = () => {
    const ids = selectedRowKeys;
    if (!ids.length) return;
    let email = "";

    Modal.confirm({
      title: `Transfer ${ids.length} location${
        ids.length > 1 ? "s" : ""
      }?`,
      content: (
        <>
          <label>Send transfer invitation to this email</label>
          <input
            type="email"
            placeholder="Agent email..."
            defaultValue=""
            onChange={(e) => (email = e.target.value)}
            style={{
              width: "80%",
              marginTop: 8,
              border: "2px solid var(--light-blue)",
              borderRadius: "12px",
              padding: "5px 5px",
            }}
          />
        </>
      ),
      okButtonProps: { className: "btn btn-primary" },
      okText: "Transfer",
      cancelText: "Cancel",
      centered: true,
      footer: (_, { OkBtn, CancelBtn }) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <CancelBtn />
          <OkBtn />
        </div>
      ),
      onOk: async () => {
        // simple validation
        if (!/\S+@\S+\.\S+/.test(email)) {
          message.error("Please enter a valid email.");
          return;
        }

        try {
          const response = await fetch(
            `${api.main}api/agent/transfer_location`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                access_token: user.access_token,
                sagent_id: user.id,
                location_ids: ids,
                to_sagent_email: email,
              }),
            }
          );

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || "An error occurred");
          }

          setSubagents((prev) =>
            prev.filter((s) => !ids.includes(s.registration_id))
          );
          setSelectedRowKeys([]);
          message.success(
            `Transferred ${ids.length} item${
              ids.length > 1 ? "s" : ""
            }`
          );
        } catch (err) {
          console.error("Error:", err);
          message.error(err.message || "Transfer failed.");
        }
      },
    });
  };

  const getStatusMeta = (row) => {
    if (row.isTransferring) {
      return { label: "Transferring", variant: "transfer" };
    }
    if (row.status === "A") {
      return { label: "Active", variant: "active" };
    }
    return { label: "Inactive", variant: "inactive" };
  };

  const columns = [
    {
      title: "Location",
      dataIndex: "business_name",
      key: "business_name",
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "business_name" ? sorter.order : null,
      render: (_, row) => (
        <div className="manage-location-cell">
          <div className="manage-location-name">{row.business_name}</div>
          <div className="manage-location-address">{formatAddress(row)}</div>
        </div>
      ),
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "Fees",
      dataIndex: "vatm_fee",
      key: "vatm_fee",
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "vatm_fee" ? sorter.order : null,
      render: (_, row) => (
        <div className="manage-location-fees">
          <div>
            <span>Fee</span>
            <strong>${Number(row.vatm_fee || 0).toFixed(2)}</strong>
          </div>
          <div>
            <span>Daily Limit</span>
            <strong>${Number(row.daily_cash_limit || 0).toFixed(2)}</strong>
          </div>
        </div>
      ),
      responsive: ["sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, row) => {
        const meta = getStatusMeta(row);
        return (
          <span
            className={`manage-location-status status-pill status-pill--${meta.variant === "transfer" ? "danger" : meta.variant === "active" ? "success" : "neutral"}`}
          >
            {meta.label}
          </span>
        );
      },
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "status" ? sorter.order : null,
      responsive: ["sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: "License",
      dataIndex: "license_id",
      key: "license_id",
      render: (v) => (
        <span
          className={`manage-location-license status-pill ${v ? "status-pill--info" : "status-pill--neutral"}`}
        >
          {v ? "Licensed" : "Unlicensed"}
        </span>
      ),
      align: "left",
      sorter: true,
      sortOrder: sorter.field === "license_id" ? sorter.order : null,
      responsive: ["md", "lg", "xl", "xxl"],
    },
    {
      title: "Actions",
      key: "Actions",
      render: (_, row) => (
        <div className="actions-column">
          <button
            className="btn btn-primary"
            onClick={(e) => {
              e.preventDefault();
              sendPasswordResetLink(row.registration_id);
            }}
          >
            Send Reset Link
          </button>
          <button
            className="manage-location-edit-button"
            onClick={() =>
              navigate(`/view-locations/${row.registration_id}`)
            }
          >
            <img
              src="/spare-edit-icon.png"
              height="15px"
              width="15px"
              alt="edit"
            />
          </button>
        </div>
      ),
      responsive: ["sm", "md", "lg", "xl", "xxl"],
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      name: record.registration_id,
      disabled: record.isTransferring,
    }),
  };

  const pagerTotalForLayout = Math.max(1, total);
  const totalPages = Math.max(1, Math.ceil(pagerTotalForLayout / pageSize));
  const safeCurrent = Math.min(current, totalPages);

  return (
    <div className="manage-locations-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Locations</p>
          <h2>Manage Merchant Locations</h2>
          <p className="section-subtitle">
            Search, bulk update, and drill into merchant storefronts.
          </p>
        </div>
        <div className="page-pill">
          {selectedRowKeys.length
            ? `${selectedRowKeys.length} selected`
            : `${total} total`}
        </div>
      </div>

      <section className="page-section manage-locations-panel">
        {loading && <div className="manage-locations-loading">Loading merchants...</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="manage-locations-toolbar data-surface-toolbar">
          <div className="manage-locations-search">
            <label htmlFor="manage-locations-search">Search locations</label>
            <input
              id="manage-locations-search"
              type="text"
              placeholder="Search by name or address…"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="manage-locations-toolbar-actions">
            <div className="manage-locations-meta data-surface-meta">
              <span className="data-surface-meta__item"><strong>{total}</strong> locations</span>
              <span className="data-surface-meta__item"><strong>{selectedRowKeys.length}</strong> selected</span>
            </div>
            <div className="manage-locations-bulk">
              <button
                className="btn btn-primary"
                disabled={!selectedRowKeys.length}
                onClick={() => bulkSetStatus("Active")}
              >
                Activate Selected
              </button>
              <button
                className="btn btn-deactivate"
                disabled={!selectedRowKeys.length}
                onClick={() => bulkSetStatus("Inactive")}
              >
                Deactivate Selected
              </button>
              <button
                className="btn btn-delete"
                disabled={!selectedRowKeys.length}
                onClick={bulkDelete}
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>

        <div className="manage-locations-table-wrapper show-scrollbar">
          <Table
            className="manage-locations-table"
            size="medium"
            rowKey={(row) => row.registration_id}
            rowClassName={(row) =>
              row.isTransferring ? "row-transferring" : ""
            }
            columns={columns}
            dataSource={pageRows}
            loading={loading}
            pagination={false}
            tableLayout="auto"
            locale={{
              emptyText: (
                <div className="table-empty-state">
                  <strong>No locations found</strong>
                  <span>Try a different search, or clear filters to view the full location list.</span>
                </div>
              ),
            }}
            rowSelection={rowSelection}
            scroll={{ y: 520 }}
            onChange={(_, __, tableSorter) => {
              const s = Array.isArray(tableSorter) ? tableSorter[0] : tableSorter;
              const next = {
                field: s?.columnKey || s?.field || null,
                order: s?.order || null,
              };
              setSorter(next);
            }}
            sticky
          />
        </div>

        <div className="manage-locations-footer data-surface-footer">
          <Pagination
            disabled={total === 0}
            className="manage-locations-pagination"
            current={safeCurrent}
            pageSize={pageSize}
            total={pagerTotalForLayout}
            showSizeChanger={false}
            showTotal={(t, range) =>
              total > 0 ? `${range[0]}-${range[1]} of ${total}` : "0-0 of 0"
            }
            onChange={(page, size) => {
              setCurrent(page);
              setPageSize(size);
            }}
          />
        </div>
      </section>
    </div>
  );
}

export default withRouter(ViewLocations);
